import React, { memo, useState, useEffect, useRef } from "react";
import { Route, Switch, useParams } from "react-router-dom";
import { useStore } from "src/store";

import api, { useAsync } from "src/api";
import { parseQs, useDebounce, useBrowserNav, setCustomHtml, insertHtmlToTag } from "src/utils";
import { Load } from "src/component-loader";
import { GenericFeed } from "src/component-placeholders";
import { Link, Page } from "src/primitives";
import { setDangerousHtml, applyKeywordMacros } from "src/utils";
import { logAdminPageBuilderUrl, logFinalProvider, logSsr2Configs } from "./airfindDebugLogs";
import ResultsSERP from "./ResultsSERP";
import ResultsGCSE from "./ResultsGCSE";

import Input from "./Input";
import styles from "./style.css";
import GoogleRecaptcha from "../../core/globals/googleRecaptcha";
import {useTranslation} from "react-i18next";
import {SPONSORED_ADS_PROVIDER_IDS} from "../../utils/constants";
import { facebookPixelTemplate } from '../../core/globals/templates.js'

const getPostbackConfig = (postbacks = []) => {
  const urlParams = new URLSearchParams(window.location.search);
  return postbacks[urlParams.get('spx')] || postbacks.find(postback => postback.default) || postbacks[0] || {}
}

const getLastSegment = (category) => {
  const segments = category.split('/');
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]) return segments[i];
  }
  return category;
};

const InputForm = memo(props => {
  const { onSubmit, searchBarNone, scrollRef, class: _class, left, img, scrollToResults, ...rest } = props;
  const ref = useRef();

  return (
    <div class={styles.searchbar__background + ' searchbar__background'} ref={scrollRef}>
      <div class={styles.search_header + ' search_header'}>
        <form
          ref={ref}
          class={_class + ' searchbar__background'}
          onSubmit={e => {
            e.preventDefault();
            if (ref.current) {
              const input = ref.current.querySelector("input");
              input && input.blur();
            }
            onSubmit(e);
          }}
        >
          {!scrollToResults && (
            <Link class={`main_logo ${searchBarNone && styles.searchBarNone}`}>
              {left || null}
              <img src={img} />
            </Link>
          )}
          {!searchBarNone && <Input {...rest} />}
        </form>
      </div>
    </div>
  );
});

const Redirect = memo(props => {
  const { to } = props;

  useEffect(() => {
    if (to) {
      window.location.href = to;
    }
  }, []);

  return null;
});

const Root = memo(props => {
  const store = useStore(["config", "children", "search"]);
  const children = store("children");
  const {
    clientid: clientId,
    brand,
    location = {},
    country,
    state,
    city,
    variant,
    repclientid: repClientId,
    googleRecaptcha,
    googleRecaptchaSampleRate,
    googleRecaptchaShowThreshold,
    googleRecaptchaSolveThreshold,
    reCaptchaScoreSiteKey,
    reCaptchaChallengeSiteKey,
    serp,
  } = store("config");

  const { match = {}, location: loc = {}, history = {}, providerOverride } = props;
  let { q, search_term, afsterm, provider = "", source = "", airFindDebug = "", p = 0 } = parseQs(loc.search || "");
  const tempQuery = store("search").query ?? q ?? search_term ?? afsterm;
  const query = applyKeywordMacros([tempQuery], { country, state, city })[0];
  const [activeSearchedQuery, setSearchedQuery] = useState(query);
  const [submitFromSearch, setSubmitFromSearch] = useState(false);
  const [scrollToResultCondition, setScrollToResultCondition] = useState();
  const [loading, setLoading] = useState();
  const {t: translator} = useTranslation();
  const [preloadedData, setPreloadedData] = useState([]);
  const [finalSearchResults, setFinalSearchResults] = useState([]);

  const setQuery = (query) => {
    store.set("search", {
      ...store("search"),
      query
    })
  }
  const inputFormRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const { lat, lng } = location;
  if(children.find(c => c.name === "bingSyndicationJS" && (c?.props.bingSyndicationJsModel == "AdsOnly" || c?.props.bingSyndicationJsModel == "Zebra") )) {
    return;
  }

  const [showAutoComp, setShowAutoComp] = useState(false);
  const [registeredSerpEvents, setRegisteredSerpEvents] = useState(false);
  const useGetAutocomplete = useAsync(api.getSearchAutocomplete, { callOnMount: false });
  const getAutocomplete = useGetAutocomplete({ query });

  const serpLayout = children.find(ch => ch.name === "serpLayout");
  const navComponentV2 = children.find(ch => ch.name === "navComponentV2");
  const serpLayoutObj = serpLayout ? JSON.parse(serpLayout.props.serpLayout) : [];
  const paginationComponent = serpLayoutObj.find(serp => serp.name == "paginationComponent");
  const sponsoredSearchResults2 = serpLayoutObj.find(serp => serp.name == "sponsoredSearchResults2") || {};
  const organicSearchResults = serpLayoutObj.find(serp => serp.name == "OrganicSearchResults") || {};
  const relatedKeywords = serpLayoutObj.find(serp => serp.name == "RelatedKeywords") || {};
  const bingSyndicationJs = children.find(c => c.name === "bingSyndicationJS")
  const clarityScripts = children.find(ch => ch.name === "clarityScripts");
  const bingClarityId = clarityScripts?.props?.clarityProjectId || false;
  const customComponentV2 = children.find(ch => ch.name === "customComponentV2");
  const yahooClarityId = customComponentV2?.props?.template === "yahooD2STrackers" ? customComponentV2?.props?.componentConfig?.yahooClarityProjectId : false
  const organicNewsResult = serpLayout ? (serpLayoutObj.find(serp => serp.name == "OrganicNewsResults") ? serpLayoutObj.find(serp => serp.name == "OrganicNewsResults") : {}) : {};
  const postbackConfig = getPostbackConfig(sponsoredSearchResults2?.ssr2Postbacks)

  const enablePreloadData = (sponsoredSearchResults2.allocGoogle == 100 && airFindDebug != 5);

  if (relatedKeywords?.enabled && relatedKeywords.relatedKeywordsPosition === 'Sidebar') {
    organicNewsResult.relatedKeywords =true;
  } else {
    organicNewsResult.relatedKeywords =false;
  }

  const styleName = sponsoredSearchResults2?.sponsoredSearchResults2Style;


  let searchParams = { lat , lng, clientId, brand, affiliateId: brand, searchTerm: query, p: +p || 0, provider: providerOverride || provider, source ,variant, repClientId, style_name : styleName, datetime : new Date().toISOString(), userLocalDatetime: new Date().toISOString()};

  if (!source && bingSyndicationJs && bingSyndicationJs.props && bingSyndicationJs.props.d2sTraffic != 'false') {
    searchParams.source = 'bingd2s';
  }
  if (bingClarityId) {
    searchParams.bingClarityId = bingClarityId;
  }
  if (yahooClarityId) {
    searchParams.yahooClarityId = yahooClarityId;
  }
  if (airFindDebug){
    searchParams.airFindDebug = airFindDebug;
  }
  if (sponsoredSearchResults2 && sponsoredSearchResults2.active) {
    searchParams.overrideSplitConfiguration = sponsoredSearchResults2.overrideSplitConfiguration;
    if(sponsoredSearchResults2.sponsoredSearchResults2D2STraffic != 'false'){
      searchParams.source = source || 'bingd2s';
    }
    if (styleName == 'LEFTCTA' || styleName == 'RIGHTCTA' || styleName == 'CARD_LAYOUT_WITH_CTA_TEXT' || styleName == 'SQAURED_CARD_LAYOUT_WITH_CTA' || styleName == 'NUMBEREDCARDSLAYOUTCTA' ||  styleName == 'CHEVRONCTA' || styleName == 'WRAPPEDNUMBEREDCARD') {
      searchParams.displayCTA = true;
    }
    if(sponsoredSearchResults2.sponsoredSearchResults2ProductAds){
      searchParams.productAds = sponsoredSearchResults2.sponsoredSearchResults2ProductAds;
    }
    searchParams.trafficType = sponsoredSearchResults2.sponsoredSearchResults2TestTraffic;
    searchParams.serpStyle = sponsoredSearchResults2.bingSponsoredStyle[sponsoredSearchResults2.sponsoredSearchResults2Style?.toUpperCase()]?.id || 10;
    searchParams.mainline = 1 * (sponsoredSearchResults2.sponsoredSearchResults2AdSlots || 8);
    searchParams.bucket = sponsoredSearchResults2.bucket || '1';
    searchParams.sessionId = window.afSessionId;
    searchParams.adUnitId =sponsoredSearchResults2.sponsoredSearchResults2AdUnitId ? sponsoredSearchResults2.sponsoredSearchResults2AdUnitId : undefined;
    searchParams.overrideAdUnitId = sponsoredSearchResults2.overrideSponsoredSearchResults2AdUnitId ? sponsoredSearchResults2.overrideSponsoredSearchResults2AdUnitId : false;
    searchParams.referrer = encodeURIComponent(document.referrer);
    // register fb script
    if(!registeredSerpEvents) {
      if(!window.fbq && postbackConfig.facebookPixelId && postbackConfig.type === 'facebookPixel') {
        const img = `
        <img height="1" width="1" style="display:none" 
        src="https://www.facebook.com/tr?id=${postbackConfig.facebookPixelId}&ev=PageView&noscript=1"/>
        `
        const replacedTemplate = facebookPixelTemplate.replace("@facebookPixelId", postbackConfig.facebookPixelId)
        insertHtmlToTag(replacedTemplate,"script","head")
        insertHtmlToTag(img, "noscript", "head")
      }
      if (postbackConfig.type === 'customJS') {
        if(postbackConfig.headSnippet) setCustomHtml(postbackConfig.headSnippet, "head");
        if(postbackConfig.bodySnippet) setCustomHtml(postbackConfig.bodySnippet, "body");
      }
      setRegisteredSerpEvents(true)
    }
  }
  serpLayout ? (searchParams.serpLayoutVariant = variant) : searchParams;
  const serpLayoutValue = serpLayout?.props?.serpLayout;
  const serpLayoutProp = serpLayoutValue ? JSON.parse(serpLayoutValue) : [];
  const hasArticlePage = serpLayoutProp.find(serp => serp.name == "ArticlePage");

  const styleVariables = sponsoredSearchResults2 && {...sponsoredSearchResults2[sponsoredSearchResults2.sponsoredSearchResults2Style], ...sponsoredSearchResults2['BASIC']};

  useEffect(() => {
    const appElement = document.getElementById('app');

    if (hasArticlePage?.active) {
      if (hasArticlePage?.serpArticleColorTheme) {
        appElement.style.setProperty(`--serpArticleColorTheme`, hasArticlePage.serpArticleColorTheme);
      }
      if (hasArticlePage?.serpArticleColorThemeSecondColor) {
        appElement.style.setProperty(`--serpArticleColorThemeSecondColor`, hasArticlePage.serpArticleColorThemeSecondColor);
      }
      if (styleVariables) {
        for (const keyProp in styleVariables) {
          appElement.style.setProperty(`--${keyProp}`, styleVariables[keyProp]);
        }
      }
    } else if (sponsoredSearchResults2?.[sponsoredSearchResults2?.sponsoredSearchResults2Style] && !hasArticlePage?.active) {
      for (const keyProp in styleVariables) {
        appElement.style.setProperty(`--${keyProp}`, styleVariables[keyProp]);
      }
    } else if (!hasArticlePage?.active) {
      for (const keyProp in sponsoredSearchResults2) {
        if (sponsoredSearchResults2[keyProp]?.toString().substring(0, 1) === "#") {
          appElement.style.setProperty(`--${keyProp}`, sponsoredSearchResults2[keyProp]);
        }
      }
      if (sponsoredSearchResults2?.[sponsoredSearchResults2?.sponsoredSearchResults2Style] && !hasArticlePage?.active) {
        for (const keyProp in styleVariables) {
          appElement.style.setProperty(`--${keyProp}`, styleVariables[keyProp]);
        }
      }
    }

    function handleResize() {
      if (window.innerWidth < 768 && sponsoredSearchResults2?.ssr2ScrollToResults) {
        setScrollToResultCondition(true);
      } else {
        setScrollToResultCondition(false);
      }
    }
    document.addEventListener("resize", handleResize);
    handleResize();

    googleRecaptcha && store.set('googleRecaptcha', new GoogleRecaptcha({ reCaptchaScoreSiteKey, reCaptchaChallengeSiteKey, googleRecaptchaSampleRate, googleRecaptchaShowThreshold, googleRecaptchaSolveThreshold }))
    return () => {
      document.removeEventListener("resize", handleResize);
    };
  }, []);

  searchParams.serveUrl = encodeURIComponent(window.location.href);
  const preloadData = () => {
    if(enablePreloadData){
    const preloadedSearchData = JSON.parse(store().children.find(item => item.name == 'serpLayout').props.serpLayout).map(
      (it) => {
          if(it.name == 'sponsoredSearchResults2') {
              it = {
                  "name": "sponsoredSearchResults2",
                  "value" : {
                      "source": "provider",
                      "customCode": JSON.stringify({
                          query: store().config.q,
                          pubId: it.sponsoredSearchResults2GcsePubId || 'partner-pub-9718435370311023',
                          channel: store().config.brand,
                          maxTop: it.sponsoredSearchResults2AdSlots || 'undefined',
                          adtest: it.sponsoredSearchResults2TestTraffic ? 'on' : 'off',
                          styleId: it.sponsoredSearchResults2StyleId || '8225118728',
                      }) }
              }
              return it
          } else {
              return it.value = it
          }

      });
      return {'final':true, content:preloadedSearchData, organicResultsMaxItems: 6, page: "0", pageSize: 10, provider: 112, routingProvider:  "contentai", statusCode: 0, statusDescription: "Success"}
    } else {
      return {}
    }
  };

  useEffect(() => {
    const preloaded = preloadData();
    setPreloadedData(preloaded);
    setFinalSearchResults(preloaded);
  }, []);
  // let useSearch = useAsync(api.getSearch, { callOnMount: enablePreloadData ? false : !!query });
  let useSearch = useAsync(api.getSearch, { callOnMount: false });
  let search = useSearch(searchParams);

  useEffect(() => {
    if (query) {
      search.call(searchParams).then((response) => {
        const combinedResults = {...preloadedData, ...response, searchFinal:true};
        setFinalSearchResults(combinedResults);
        setQuery(searchParams.searchTerm)
        setSearchedQuery(searchParams.searchTerm);
      });
    }
  }, []);

  useBrowserNav((location) => {
    const q = new URLSearchParams(location.search).get("q");
    const pageNumber = new URLSearchParams(location.search).get("p");
    const searchTerm = applyKeywordMacros([q], { country, state, city })[0];
    const searchStoreParams = {
      ...store("search"),
      query: searchTerm,
      page: pageNumber ?? 0
    };
    store.set("search", searchStoreParams);
    search.call({ ...searchParams, searchTerm, p: pageNumber || undefined, datetime: new Date().toISOString(), userLocalDatetime: new Date().toISOString() });
    setSearchedQuery(searchTerm);
    setSubmitFromSearch(true);
  }, [search, query]);

  if (sponsoredSearchResults2 && sponsoredSearchResults2.active ){
    searchParams.jsonAdTag = true
    searchParams.hitHighlighting = sponsoredSearchResults2.advancedSponsoredSERP2HitHighlighting;
    searchParams.sponsoredResultsImages = sponsoredSearchResults2.advancedSponsoredSERP2ShowImagesForSponsoredResults;
    searchParams.productAdsLabels = sponsoredSearchResults2.advancedSponsoredSERP2ShowLabelsForProductAds;
    searchParams.productAdsRatings = sponsoredSearchResults2.advancedSponsoredSERP2ShowRatingsOnProductAds;
  }
  if ((organicSearchResults && organicSearchResults.enabled) || (relatedKeywords && relatedKeywords.enabled)){
    searchParams = {...searchParams, ...{jsonAdTag:true, bingOneCall:true}}
  }

  const submitSearch = useDebounce(
    (val, override) => {
      const useYahooV2 = search?.data?.useYahooV2;
      setSubmitFromSearch(true);
      setLoading(true);
      let addPageNumber;
      const v = !val && val !== "" ? query : val;
      const searchTerm = applyKeywordMacros([v], { country, state, city })[0];
      const searchProvider = search?.data?.provider;
      const isYahoo = searchProvider === SPONSORED_ADS_PROVIDER_IDS.YAHOO_SYNDICATION.id;
      const numberPagination = !isYahoo || isYahoo && useYahooV2;

      const searchStoreParams = {
        ...store("search"),
        query: searchTerm,
        p: override?.p ?? store("search").p
      };

      store.set("search", searchStoreParams);
      setShowAutoComp(false);
      const { page, variant, provider } = parseQs(window.location.search);
      if (numberPagination) addPageNumber = override?.p ? `&p=${override.p}` : "";

      history.push({
        pathname: match.pathname,
        search: `?q=${searchTerm}${page ? "&page=" + page : ""}${variant ? "&variant=" + variant : ""}${
          provider ? "&provider=" + provider : ""
        }${addPageNumber || ""}`
      });

      if (!useYahooV2) {
        if (override?.direction === 'next' && finalSearchResults.providerNextArgs) {
          const decodedNextArgs = decodeURIComponent(finalSearchResults.providerNextArgs);
          searchParams = {
            ...searchParams,
            nextArgs: encodeURIComponent(decodedNextArgs),
          }
        }

        if (override?.direction === 'prev' && finalSearchResults.providerPrevArgs) {
          const decodePrevArgs = decodeURIComponent(finalSearchResults.providerPrevArgs);
          searchParams = {
            ...searchParams,
            prevArgs:  encodeURIComponent(decodePrevArgs),
          }
        }
      }

        const now = new Date();

        search.call({ ...searchParams, ...override, submitFromSearch: true, searchTerm: v, datetime: now.toISOString(), userLocalDatetime: now.toISOString() }, submitSearch).then((response) => {
        if (!isYahoo) {
          const combinedResults = {...preloadedData, ...response};
          setFinalSearchResults(combinedResults);
        }
        if (scrollToResultCondition) {
          const sponsoredContent = document.getElementById("sponsoredCtrlId");
          const organicContent = document.getElementById("organicCtrlId");
          if (sponsoredContent) {
            sponsoredContent.scrollIntoView({ behavior: "smooth" });
          } else if (organicContent) {
            organicContent.scrollIntoView({ behavior: "smooth" });
          }
        }
      });
      setSearchedQuery(searchTerm);
      setLoading(search.loading);
    },
    500,
    [search.loading] // To update the query variable
  );

  const footer = children.find(ch => ch.name === "Footer");
  const searchBar = children.find(ch => ch.name === "SearchBar");
  const {
    searchBarPlaceholderText = translator('Home.Search the web'),
    autocomplete: autocompleteProp = false,
    bingTrendingTerms = 0,
    enhancedByGoogleLogoVisibility = true,
    searchBarStyle = '',
  } = searchBar ? searchBar.props || {} : {};


  const autocomplete = provider === "bing" && bingTrendingTerms ? false : autocompleteProp;
  const [, autocompleteResults = []] = Array.isArray(getAutocomplete.data) ? getAutocomplete.data : [];
  const { routingProvider } = finalSearchResults;

  const finalProvider = routingProvider || providerOverride || provider;

  const updateSearchAutocomplete = useDebounce(value => {
    autocomplete && getAutocomplete.call({ query: value });
  }, 500);

  const setSearch = val => {
    const v = !val && val !== "" ? query : val;
    setQuery(v);
    updateSearchAutocomplete(v);
  };
  // const searchBarStyle = 1;

  let templateSearchBarStyle;
  if (
    sponsoredSearchResults2 &&
    sponsoredSearchResults2.searchBarStyle &&
    sponsoredSearchResults2.sponsoredSearchResults2SearchBarStyle &&
    sponsoredSearchResults2.active.toString() === "true"
  ) {
    templateSearchBarStyle = sponsoredSearchResults2.searchBarStyle[sponsoredSearchResults2.sponsoredSearchResults2SearchBarStyle]?.style || '';
  }

  useEffect(()=> {
    store.set("search", {
      query: q || search_term,
      match,
      history,
      p: +p || search?.data?.p || store("search").p || 0
    })
  },[]) // Initialize the search state

  useEffect(() => {
    if (finalProvider) {
      logAdminPageBuilderUrl();
      logFinalProvider(finalProvider);
    }
    if (sponsoredSearchResults2 && finalProvider === "bing") {
      logSsr2Configs(sponsoredSearchResults2);
    }
    if (scrollToResultCondition) {
      scrollIntervalRef.current = setInterval(() => {
        if (window.scrollY === 100) {
          clearInterval(scrollIntervalRef.current);
        } else {
          scrollToInputForm();
        }
      }, 50);
      return () => clearInterval(scrollIntervalRef.current);
    }
  }, [finalProvider]);

  let img =
      finalProvider === "googlecse-inline" ? undefined :
          finalProvider === "googlecse" ? enhancedByGoogleLogoVisibility === false ? undefined
                  : `${window.GLOBALS.ASSETS.GENERIC}/img/search/enhanced_by_google.png`
              : searchBarStyle === "1"
                  ? "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzciIGhlaWdodD0iNDYiIHZpZXdCb3g9IjAgMCAzNyA0NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxLjgyNzEgMjUuODk4NUMxOS44NDY2IDI2Ljk4NiAxNy41NDYxIDI3LjMxOTMgMTUuMzQzNCAyNi44MzhDMTMuMTQwNiAyNi4zNTY3IDExLjE4MTUgMjUuMDkyNiA5LjgyMTY1IDIzLjI3NTNDOC40NjE4MyAyMS40NTggNy43OTE0NSAxOS4yMDc4IDcuOTMyMiAxNi45MzMzQzguMDcyOTQgMTQuNjU4OCA5LjAxNTQ4IDEyLjUxMDggMTAuNTg4NyAxMC44NzlDMTIuMTYyIDkuMjQ3MzEgMTQuMjYxNiA4LjI0MDA5IDE2LjUwNjYgOC4wNDAyM0MxOC43NTE1IDcuODQwMzYgMjAuOTkyOSA4LjQ2MTA5IDIyLjgyMzkgOS43ODk3NEMyNC42NTQ4IDExLjExODQgMjUuOTU0IDEzLjA2NjkgMjYuNDg1NiAxNS4yODE1QzI3LjAxNzIgMTcuNDk2MiAyNi43NDU5IDE5LjgzMDIgMjUuNzIxIDIxLjg1OThMMzEuMjg3OSAyNy45MDMzQzMzLjQ4MTggMjQuODk2NCAzNC42NjI4IDIxLjI1NjUgMzQuNjU3IDE3LjUxOThDMzQuNjU3IDcuODM4MjEgMjYuODk4OCAtMC4wMDcyMDIxNSAxNy4zMjQ4IC0wLjAwNzIwMjE1QzcuNzUwODggLTAuMDA3MjAyMTUgLTAuMDA3MzI0MjIgNy44MzgyMSAtMC4wMDczMjQyMiAxNy41MTk4Qy0wLjAwNzMyNDIyIDI3LjIwMTQgNy43NTA4OCAzNS4wNDY4IDE3LjMyNDggMzUuMDQ2OEMxOC40MjM1IDM1LjA0NjkgMTkuNTE5OCAzNC45NDE5IDIwLjU5ODkgMzQuNzMzTDMwLjEyOTIgNDUuMDc0N0MzMC4zODU4IDQ1LjM1MzMgMzAuNjk0MiA0NS41NzgxIDMxLjAzNjggNDUuNzM2MkMzMS4zNzkzIDQ1Ljg5NDMgMzEuNzQ5MyA0NS45ODI2IDMyLjEyNTYgNDUuOTk2MUMzMi41MDE5IDQ2LjAwOTYgMzIuODc3MiA0NS45NDggMzMuMjI5OSA0NS44MTQ4QzMzLjU4MjcgNDUuNjgxNiAzMy45MDYxIDQ1LjQ3OTUgMzQuMTgxNiA0NS4yMTk5TDM2Ljk5ODUgNDIuNTY1OEwyMS44MjcxIDI1Ljg5ODVaIiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXJfMjBfMykiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl8yMF8zIiB4MT0iMTEuMzk4OSIgeTE9IjEuMDUwMjYiIHgyPSIyOC41MTM4IiB5Mj0iNDcuNTUxNiIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgo8c3RvcCBvZmZzZXQ9IjAuMDkiIHN0b3AtY29sb3I9IiM1MUNFRkQiLz4KPHN0b3Agb2Zmc2V0PSIwLjU5IiBzdG9wLWNvbG9yPSIjMDAzNEY1Ii8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPC9zdmc+Cg=="
                  : finalProvider === 'bing' ? `${window.GLOBALS.ASSETS.ROOT}/ext/50012/1627665338105-search-logo.png` : finalProvider === 'yahoosyndication' ?
                      "https://cdn2.airfind.com/yahoo_search.png" : '';

  if(finalProvider !== "googlecse-inline" && finalProvider !== "googlecse" && serp && serp.logo) {
    img = serp.logo
  }

  const scrollToInputForm = () => {
    document.getElementById("page-cmp-container").style.position = "relative";
    inputFormRef.current?.scrollIntoView();
  };

  const hasSearched = Object.keys(finalSearchResults).length > 0;
  let isLoading = scrollToResultCondition ? loading : (search.loading);
  // eslint-disable-next-line no-prototype-builtins
  isLoading = finalSearchResults.hasOwnProperty('final') ? !finalSearchResults.final : isLoading;

  if (!search.loading && hasSearched) {
    setLoading(false);
  } else if (search.loading && !hasSearched) {
    setLoading(true);
  } else if (!search.loading && !hasSearched) {
    setLoading(false);
  }

  const [isLoaded, setIsLoaded] = useState(!!searchParams.searchTerm);

  const currentUrl = new URL(window.location.href)
  const categoryAi = currentUrl.searchParams.get("category")
  const { articleId:articleIdDynamic } = useParams();
  const fetchData = async () => {
    if (!searchParams.searchTerm) {
      const response = await api.getContentAiArticles({
        category: encodeURIComponent(categoryAi),
        useCache: true,
        lang: hasArticlePage?.serpLanguage || "english",
        take: 1,
        orderStrategy: "newest",
        clientId: clientId,
      });

      const extractedTerm = response[0]?.keyword || getLastSegment(categoryAi);
      setQuery(extractedTerm);
      setSearchedQuery(extractedTerm);
      setIsLoaded(true);
    }
};

  useEffect(() => {
    if (hasArticlePage?.active) {
      fetchData();
    }
  },[]);

  if (hasArticlePage?.active) {
    if (!isLoaded) {
      return <div></div>;
    }

    return (
      <Load
        hasArticlePage={hasArticlePage}
        navComponentV2={navComponentV2}
        results={finalSearchResults}
        searchParams={{ ...searchParams, articleIdDynamic: (articleIdDynamic || activeSearchedQuery).replace(/-/g, " "), activeSearchedTerm: activeSearchedQuery, submitFromSearch }}
        serpLayout={serpLayoutProp}
        paginationEnabled={!paginationComponent || paginationComponent.enabled}
        submitSearch={submitSearch}
        organicNewsResult={organicNewsResult}
        name="ArticlePage"
      />
    );
  }

  return (
    <Page
      header={
        loading ? (
          <GenericFeed count={6} />
        ) : (
          <>
            <style ref={setDangerousHtml(templateSearchBarStyle)}></style>
            {scrollToResultCondition ? (
              <div className={styles.tallHeader}>
                <Link class={`main_logo ${sponsoredSearchResults2?.sponsoredSearchResults2SearchBarStyle === "None" && styles.searchBarNone}`}>
                  {props.left || null}
                </Link>
                <img src={img} className={styles.logo} />
              </div>
            ) : null}
            {(!sponsoredSearchResults2?.sponsoredSearchResults2SearchBarPosition ||
              sponsoredSearchResults2?.sponsoredSearchResults2SearchBarPosition === "Top") && (
              <InputForm
                class={searchBarStyle === "1" ? styles.inputFormHeader1 : styles.inputFormHeader}
                img={img}
                scrollRef={inputFormRef}
                onSubmit={() => submitSearch(query)}
                provider={finalProvider}
                autocompleteResults={showAutoComp ? autocompleteResults : undefined}
                submitSearch={submitSearch}
                value={query}
                placeholder={searchBarPlaceholderText}
                scrollToResults={scrollToResultCondition}
                onInput={e => {
                  setSearch(e.target.value);
                  setShowAutoComp(true);
                }}
                searchBarNone={sponsoredSearchResults2?.sponsoredSearchResults2SearchBarStyle === "None"}
                searchBarStyle={searchBarStyle}
                setQuery={text => setQuery(text)}
                onClick={scrollToResultCondition ? () => scrollToInputForm() : () => {}}
              />
            )}
          </>
        )
      }
      closeIcon={document.body.dir == "rtl" ? "chevron_right" : "chevron_left"}
      searchIcon={false}
      scrollToResults={sponsoredSearchResults2?.ssr2ScrollToResults}
      onClose={() => history.push && history.push("/")}
    >
      <div class={searchBarStyle === "1" ? styles.containerStyle1 : "" + " page__background"}>
        {isLoading ? (
          <GenericFeed count={6} />
        ) : !hasSearched ? (
          <div class={styles.noResults}>
            <strong>Please enter something.</strong>
            <br />
            to begin your search
          </div>
        ) : finalSearchResults.redirectURL ? (
          <Redirect to={finalSearchResults.redirectURL} />
        ) : finalProvider.startsWith("googlecse") ? (
          <ResultsGCSE results={finalSearchResults} />
        ) : (
          <div>
            <ResultsSERP
              results={finalSearchResults}
              searchParams={{ ...searchParams, activeSearchedTerm: activeSearchedQuery, submitFromSearch }}
              serpLayout={serpLayoutProp}
              paginationEnabled={!paginationComponent || paginationComponent.enabled}
              submitSearch={submitSearch}
              organicNewsResult={organicNewsResult}
            />
          </div>
        )}
        {sponsoredSearchResults2?.sponsoredSearchResults2SearchBarPosition === "Bottom" && (
          <InputForm
            class={searchBarStyle === "1" ? styles.inputFormHeader1 : styles.inputFormHeader}
            img={img}
            scrollRef={inputFormRef}
            onSubmit={() => submitSearch(query)}
            provider={finalProvider}
            autocompleteResults={showAutoComp ? autocompleteResults : undefined}
            submitSearch={submitSearch}
            value={query}
            placeholder={searchBarPlaceholderText}
            scrollToResults={scrollToResultCondition}
            onInput={e => {
              setSearch(e.target.value);
              setShowAutoComp(true);
            }}
            searchBarNone={sponsoredSearchResults2?.sponsoredSearchResults2SearchBarStyle === "None"}
            searchBarStyle={searchBarStyle}
            setQuery={text => setQuery(text)}
            onClick={scrollToResultCondition ? () => scrollToInputForm() : () => {}}
          />
        )}
        {footer && <Load name="Footer" {...footer.props} />}
      </div>
    </Page>
  );
});

const Search = memo(() => {
  return (
    <Switch>
      <Route exact path="/_search" component={Root} />
      <Route exact path="/_search/:articleId" component={Root} />
      <Route exact path="/_gcse" component={props => <Root {...props} providerOverride="googlecse-inline" />} />
    </Switch>
  );
});

export default Search;
